import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchProductsBySalesType,
  fetchProductsByCategoryAndSubcategory,
  getAllCategories,
} from '../../../services/product-api.js';

const SALES_OFFERS = 'sales-offers';
const DEFAULT_LIMIT = 40;

// Legacy product salesType values that don't match their subcategory slug.
// Any other Sales & Offers subcategory (e.g. a new "anniversary" or "xmas"
// sale) matches products whose salesType equals its slug.
const LEGACY_SALES_TYPES = {
  discounts: 'discount-sales',
  clearance: 'clearance-sales',
  promotions: 'promotion-sales',
};

const getSalesType = (subCategorySlug) =>
  LEGACY_SALES_TYPES[subCategorySlug] ?? subCategorySlug;

// Shared by the page and by prefetching so both hit the same cache entry.
export const subCategoryProductsQuery = (
  category,
  subCategory,
  page = 1,
  limit = DEFAULT_LIMIT
) => {
  if (category === SALES_OFFERS) {
    const salesType = getSalesType(subCategory);
    return {
      queryKey: ['products-by-sales-type', salesType, page, limit],
      queryFn: () => fetchProductsBySalesType(salesType, page, limit),
    };
  }

  return {
    queryKey: ['products-by-category', category, subCategory, page, limit],
    queryFn: () =>
      fetchProductsByCategoryAndSubcategory(category, subCategory, limit, page),
  };
};

// Returns props to spread on a link to `/:category/:sub_category` so its
// products start loading on hover/focus/touch, before the click lands.
export const usePrefetchSubCategory = () => {
  const queryClient = useQueryClient();

  const prefetch = useCallback(
    (to) => {
      const [category, subCategory] = String(to || '')
        .split('/')
        .filter(Boolean);
      if (!category || !subCategory) return;
      // Respect Data Saver: don't spend bandwidth on pages that may not be opened
      if (navigator.connection?.saveData) return;
      queryClient.prefetchQuery(
        subCategoryProductsQuery(category, subCategory)
      );
    },
    [queryClient]
  );

  return useCallback(
    (to) => ({
      onMouseEnter: () => prefetch(to),
      onFocus: () => prefetch(to),
      onTouchStart: () => prefetch(to),
    }),
    [prefetch]
  );
};

export const useSubCategoryPage = (limit = DEFAULT_LIMIT) => {
  const [page, setPage] = useState(1);
  const { category, sub_category } = useParams();
  const queryClient = useQueryClient();

  const {
    data,
    isFetching: isFetchingProducts,
    isError: isProductsError,
    refetch: refetchProducts,
  } = useQuery({
    ...subCategoryProductsQuery(category, sub_category, page, limit),
    enabled: !!category,
  });

  // Warm the next page so "Next" in the pagination renders instantly.
  const serverTotalPages = data?.data?.totalPages;
  useEffect(() => {
    if (!category || !serverTotalPages) return;
    if (page >= serverTotalPages) return;
    queryClient.prefetchQuery(
      subCategoryProductsQuery(category, sub_category, page + 1, limit)
    );
  }, [queryClient, category, sub_category, page, limit, serverTotalPages]);

  const {
    data: allCategories,
    isLoading: isLoadingCategories,
    isFetching: isFetchingCategories,
  } = useQuery({
    queryKey: ['categories'],
    queryFn: getAllCategories,
  });

  // Memoize products to prevent new array references on every render
  const products = useMemo(() => data?.data?.products || [], [data]);
  const totalPages = data?.data?.totalPages;

  const totalSubCategoryProducts = +data?.data?.total;

  const categories = Array.isArray(allCategories?.data?.categories)
    ? allCategories.data.categories
    : [];

  const isValidSubcategory = categories?.some((cat) => {
    return (
      cat.slug.toLowerCase() === category?.toLowerCase() &&
      cat.subcategories?.some(
        (sub) => sub.slug.toLowerCase() === sub_category?.toLowerCase()
      )
    );
  });

  return {
    category,
    sub_category,

    page,
    setPage,
    totalPages,
    totalSubCategoryProducts,

    products,
    isFetchingProducts,
    isProductsError,
    refetchProducts,

    isLoadingCategories: isLoadingCategories || isFetchingCategories,
    isValidSubcategory,
  };
};
